<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { DatePicker, FilterModal, IconImage, SearchCombobox, ThemeProvider } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { formatDateForApi, formatDateToPtBR, parseCivilDate } from '@/shared/lib';
  import { usePermittedMembers } from '@/entities/User';
  import { positionApi, type Position } from '@/entities/Position';
  import {
    TRAINING_CONTENT_EXTENSION_OPTIONS,
    TRAINING_CONTENT_TYPE_OPTIONS,
    type TrainingContentExtension,
    type TrainingContentFilters,
    type TrainingContentType,
  } from '@/entities/Training';

  const props = defineProps<{
    currentFilters?: TrainingContentFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: TrainingContentFilters];
    'clear-filters': [];
  }>();

  const {
    userOptions: memberOptions,
    isLoading: isMembersLoading,
    fetchUsers,
  } = usePermittedMembers({ includeInactiveMembers: true });

  const positions = ref<Position[]>([]);
  const isPositionsLoading = ref(false);

  const memberSearchQuery = ref('');
  const positionSearchQuery = ref('');

  const draftExtension = ref<TrainingContentExtension | undefined>(undefined);
  const draftContentType = ref<TrainingContentType | undefined>(undefined);
  const draftMemberId = ref('');
  const draftPositionId = ref('');
  const draftCreatedAt = ref<Date | null>(null);
  const isCreatedAtOpen = ref(false);

  const memberComboboxOptions = computed(() => {
    const query = memberSearchQuery.value.trim().toLowerCase();
    return memberOptions.value
      .filter((member) => !query || member.name.toLowerCase().includes(query))
      .map((member) => ({ value: String(member.id), label: member.name }));
  });

  const positionComboboxOptions = computed(() => {
    const query = positionSearchQuery.value.trim().toLowerCase();
    return positions.value
      .filter((position) => !query || position.name.toLowerCase().includes(query))
      .map((position) => ({ value: String(position.id), label: position.name }));
  });

  const createdAtLabel = computed(() =>
    draftCreatedAt.value ? formatDateToPtBR(draftCreatedAt.value) : 'Selecione uma data'
  );

  function loadFromFilters(filters?: TrainingContentFilters) {
    draftExtension.value = filters?.extension;
    draftContentType.value = filters?.contentType;
    draftMemberId.value = filters?.responsibleMemberId ? String(filters.responsibleMemberId) : '';
    draftPositionId.value = filters?.responsiblePositionId
      ? String(filters.responsiblePositionId)
      : '';
    draftCreatedAt.value = parseCivilDate(filters?.createdAt);
  }

  async function loadPositions() {
    isPositionsLoading.value = true;
    try {
      const response = await positionApi.getAllPositions({ itemsPerPage: 1000 });
      positions.value = response.data || [];
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    } finally {
      isPositionsLoading.value = false;
    }
  }

  onMounted(async () => {
    await Promise.all([loadPositions(), fetchUsers()]);
    loadFromFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (filters) => loadFromFilters(filters),
    { deep: true }
  );

  function toggleExtension(value: TrainingContentExtension) {
    draftExtension.value = draftExtension.value === value ? undefined : value;
  }

  function toggleContentType(value: TrainingContentType) {
    draftContentType.value = draftContentType.value === value ? undefined : value;
  }

  function handleApply() {
    emit('update:filter', {
      extension: draftExtension.value,
      contentType: draftContentType.value,
      responsibleMemberId: draftMemberId.value ? Number(draftMemberId.value) : undefined,
      responsiblePositionId: draftPositionId.value ? Number(draftPositionId.value) : undefined,
      createdAt: draftCreatedAt.value
        ? (formatDateForApi(draftCreatedAt.value) ?? undefined)
        : undefined,
    });
  }

  function handleClear() {
    loadFromFilters(undefined);
    memberSearchQuery.value = '';
    positionSearchQuery.value = '';
    emit('update:filter', {});
  }
</script>

<template>
  <FilterModal
    content-class="w-106"
    @close="emit('close')"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="grid grid-cols-2 gap-6 px-6 py-6 border-b border-gray-02">
          <div>
            <h4 class="text-sm font-medium text-gray-07 pb-4">Extensão</h4>
            <div
              data-testid="training-content-extension-options"
              class="flex flex-col items-start gap-1"
            >
              <button
                v-for="option in TRAINING_CONTENT_EXTENSION_OPTIONS"
                :key="option.value"
                type="button"
                :data-testid="`training-content-extension-${option.value}`"
                class="flex items-center justify-start py-2 px-4 rounded-lg w-full text-sm"
                :class="
                  draftExtension === option.value
                    ? 'bg-(--theme-secondary) text-white'
                    : 'text-gray-07'
                "
                @click="toggleExtension(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>

          <div>
            <h4 class="text-sm font-medium text-gray-07 pb-4">Tipo de Conteúdo</h4>
            <div
              data-testid="training-content-type-options"
              class="flex flex-col items-start gap-1"
            >
              <button
                v-for="option in TRAINING_CONTENT_TYPE_OPTIONS"
                :key="option.value"
                type="button"
                :data-testid="`training-content-type-${option.value}`"
                class="flex items-center justify-start py-2 px-4 rounded-lg w-full text-sm"
                :class="
                  draftContentType === option.value
                    ? 'bg-(--theme-secondary) text-white'
                    : 'text-gray-07'
                "
                @click="toggleContentType(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="px-6 py-6 border-b border-gray-02">
          <h4 class="text-sm font-medium text-gray-07 pb-4">Colaborador</h4>
          <SearchCombobox
            :model-value="draftMemberId"
            :options="memberComboboxOptions"
            :loading="isMembersLoading"
            placeholder="Selecione um colaborador"
            search-placeholder="Buscar colaborador..."
            trigger-icon="arrow-down"
            class="w-full h-11"
            @update:model-value="draftMemberId = $event || ''"
            @search="memberSearchQuery = $event"
          />
        </div>

        <div class="px-6 py-6 border-b border-gray-02">
          <h4 class="text-sm font-medium text-gray-07 pb-4">Cargo</h4>
          <SearchCombobox
            :model-value="draftPositionId"
            :options="positionComboboxOptions"
            :loading="isPositionsLoading"
            placeholder="Selecione um cargo"
            search-placeholder="Buscar cargo..."
            trigger-icon="arrow-down"
            class="w-full h-11"
            @update:model-value="draftPositionId = $event || ''"
            @search="positionSearchQuery = $event"
          />
        </div>

        <div class="px-6 py-6">
          <h4 class="text-sm font-medium text-gray-07 pb-4">Data da Criação</h4>
          <button
            type="button"
            data-testid="training-content-created-at-trigger"
            class="flex w-[182px] items-center gap-2 bg-white-01 px-4 py-2.5 border border-gray-02 rounded-lg text-sm text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium"
            @click="isCreatedAtOpen = true"
          >
            <IconImage :src="CalendarIcon" alt="Calendário" class="h-5 w-5" />
            <span>{{ createdAtLabel }}</span>
          </button>
          <DatePicker
            v-model:selected-date="draftCreatedAt"
            v-model:is-open="isCreatedAtOpen"
            @apply="isCreatedAtOpen = false"
            @cancel="isCreatedAtOpen = false"
          />
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
